/**
 * The product rows the employee side nav shows, derived from one set of
 * visibility flags. The nav and any page that lists the user's benefits read
 * this same function, so the two cannot drift.
 */
export interface BenefitFlags {
  isTaOnly: boolean;
  isParticipant: boolean;
  isFamilyMember: boolean;
  showTuitionAssistance: boolean;
  showWellness: boolean;
  showPslf: boolean;
  showSlr: boolean;
  showSyf: boolean;
  showIdr: boolean;
  isLpnEnabled: boolean;
}

export interface BenefitRows {
  /** A tuition-assistance-only employee whose company has a launched TA program. */
  isTaOnlyMember: boolean;
  /** A participant or a family member: someone with a member-facing rail. */
  isMember: boolean;
  /** Members other than family members; the previous rail hid these rows from family. */
  showMemberOnly: boolean;
  tuitionAssistance: boolean;
  wellness: boolean;
  pslf: boolean;
  slr: boolean;
  learningPartners: boolean;
  loanSummary: boolean;
  syf: boolean;
  idr: boolean;
}

/**
 * Decides which benefit rows a user sees.
 *
 * @param flags product enablement and role flags for the current user and company
 * @returns one boolean per design-named row, plus the member gates they derive from
 */
export function benefitRows(flags: BenefitFlags): BenefitRows {
  const isTaOnlyMember = Boolean(flags.isTaOnly && flags.showTuitionAssistance);
  const isMember = Boolean(flags.isParticipant || flags.isFamilyMember);
  const showMemberOnly = isMember && !flags.isFamilyMember;

  return {
    isTaOnlyMember,
    isMember,
    showMemberOnly,
    tuitionAssistance: Boolean(isTaOnlyMember || (showMemberOnly && flags.showTuitionAssistance)),
    wellness: showMemberOnly && Boolean(flags.showWellness),
    pslf: showMemberOnly && Boolean(flags.showPslf),
    slr: isMember && Boolean(flags.showSlr),
    learningPartners: Boolean(flags.isLpnEnabled && (isMember || isTaOnlyMember)),
    loanSummary: isMember,
    syf: showMemberOnly && Boolean(flags.showSyf),
    idr: showMemberOnly && Boolean(flags.showIdr),
  };
}
